import { Timestamp } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import { PyhapaivatService } from '@lemon/core/service/pyhapaiva.service'
import { TimestampProvider } from '@lemon/core/service/timestamp-provider.interface'
import { FirestoreProvider, FirestoreWriteBatch } from '../../service/lasku/lasku-tallennus.service'

export type AjastetunTyonTyyppi =
  | 'asiakkuuden-paattaminen-deaktivoi-kayttajat'
  | 'asiakkuuden-paattaminen-poista-muut-kuin-tosite-roolit'
  | 'asiakkuuden-paattaminen-laheta-paattynyt-email'
  | 'asiakkuuden-paattaminen-laheta-paattymassa-email'
  | 'tilinpaatos-allekirjoittamatta'
  | 'poytakirja-allekirjoittamatta'
  | 'pankkiyhteysvaltuutus-loppunut'
  | 'lopeta-apix-maksujen-vastaanotto'
  | 'kaynnista-yksi-kerrallaan-driver-uudelleen'
  | 'aineisto-lataamatta'
  | 'holvi-customer-queue'
  | 'aktivoi-maksutavan-palveluntarjoajan-vaihto'
  | 'palveluntarjoajan-vaihto-yhteystarkistus'

export interface AjastettuTyo<T> {
  /** Jos tämä on asiakaskohtainen työ, täytä tämä */
  asiakasAvain?: string
  /** Jos tämä on käyttäjäkohtainen työ, täytä tämä. Jos tämä on täytetty, täytä myös asiakasAvain! */
  kayttajaAvain?: string
  /** Työn avain */
  avain: string
  /** täysi polku, johon työ (tyodata propertyn sisältö) siirretään */
  kohde: string
  /** */
  tyodata: T
  /** Milloin työ on ajastettu ajettavaksi */
  ajoaika: Timestamp
  /** Milloin työ ajettiin */
  ajettu: Timestamp | null
  /** Tyyppi suodattamista varten, esim. admin käyttöliittymää varten */
  tyyppi: AjastetunTyonTyyppi
  /** Lisäselite työstä, esim. admin käyttöliittymää varten */
  selite: string
}

export class AjastettuTyoService {
  ajastetunTyonTyypit: { tyyppi: AjastetunTyonTyyppi; teksti: string }[] = [
    { tyyppi: 'asiakkuuden-paattaminen-deaktivoi-kayttajat', teksti: 'Poistaa asiakkaan kaikkien käyttäjien kaikki roolit ja deaktivoi kaikki käyttäjät' },
    { tyyppi: 'asiakkuuden-paattaminen-poista-muut-kuin-tosite-roolit', teksti: 'Poistaa asiakkaan muut kuin tositteen toimittamiseen liittyvät roolit' },
    { tyyppi: 'asiakkuuden-paattaminen-laheta-paattymassa-email', teksti: 'Lähettää yhteyshenkilöille sähköpostin, joka muistuttaa sopimuksen loppumisesta' },
    { tyyppi: 'asiakkuuden-paattaminen-laheta-paattynyt-email', teksti: 'Lähettää yhteyshenkilöille sähköpostin, joka kertoo asiakkuudeen päättyneen' },
    { tyyppi: 'tilinpaatos-allekirjoittamatta', teksti: 'Lähettää yhteyshenkilöille SMS-muistutuksen allekirjoittamattomasta tilinpäätöksestä' },
    { tyyppi: 'poytakirja-allekirjoittamatta', teksti: 'Lähettää yhteyshenkilöille SMS-muistutuksen allekirjoittamattomasta yhtiökokouksen pöytäkirjasta' },
    { tyyppi: 'pankkiyhteysvaltuutus-loppunut', teksti: 'Lähettää pankkivaltuutuksen antajalle SMS-muistutuksen pankkiyhteysvaltuutuksen uusimisesta' },
    { tyyppi: 'lopeta-apix-maksujen-vastaanotto', teksti: 'Lopettaa asiakkaan Apixin verkkolaskujen vastaanoton sopimuksen ja tarvittaessa deaktivoi maksut' },
    { tyyppi: 'kaynnista-yksi-kerrallaan-driver-uudelleen', teksti: 'Käynnistää yksi kerrallaan -ajurin uudelleen' },
    { tyyppi: 'aineisto-lataamatta', teksti: 'Lähettää tarvittaessa yhteyshenkilöille SMS-muistutuksen lataamattomasta aineistosta' },
    { tyyppi: 'holvi-customer-queue', teksti: 'Ajastettu tietojen lataus Holvin API:sta' },
    { tyyppi: 'aktivoi-maksutavan-palveluntarjoajan-vaihto', teksti: 'Aktivoi ajastettu maksutavan palveluntarjoajan vaihto' },
    { tyyppi: 'palveluntarjoajan-vaihto-yhteystarkistus', teksti: 'Tarkistaa onko maksutavan pankkiyhteys katkennut ja aikaistaa ajastetun palveluntarjoajan vaihdon' },
  ]

  constructor(
    private _dateService: DateService,
    private _timestampService: TimestampProvider,
    private _pyhapaivatService: PyhapaivatService,
  ) {}

  collectionName = 'tyojono-ajastetut'
  collectionNameHistory = 'tyojono-ajastetut-historia'

  /** Get type and text */
  getAjastetunTyonTyypit() {
    return this.ajastetunTyonTyypit
  }

  /** Ajasta työ, tai ylikirjoita se kokonaisuudessaan jos se on jo olemassa. */
  public ajasta<T>(tyo: AjastettuTyo<T>, firestore: FirestoreProvider, batch: FirestoreWriteBatch) {
    const doc = firestore.annaDoc(this.collectionName + '/' + tyo.avain)
    batch.set(doc, tyo)
  }

  /** Ajastettu työ, tai ylikirjoita se kokonaisuudessaan jos se on jo olemassa. Käynnistyspäivää muutettaan, jotta se olisi AINA arkipäivä klo 12 (UTC). */
  public ajastaJaVarmistaSuomalainenLiikeaAikaJaArkipaiva<T>(tyo: AjastettuTyo<T>, firestore: FirestoreProvider, batch: FirestoreWriteBatch) {
    const ajoaika = this._dateService.timestampToDate(tyo.ajoaika)
    if (!ajoaika) {
      throw new Error('No ajoaika date present!')
    }

    if (ajoaika.getUTCHours() < 6) {
      ajoaika.setUTCHours(6)
      ajoaika.setUTCMinutes(this._getRandomMinutes())
      ajoaika.setUTCSeconds(0)
      ajoaika.setUTCMilliseconds(0)
    }
    if (ajoaika.getUTCHours() > 13) {
      ajoaika.setUTCHours(13)
      ajoaika.setUTCMinutes(this._getRandomMinutes())
      ajoaika.setUTCSeconds(0)
      ajoaika.setUTCMilliseconds(0)
    }

    const safeTime = this._pyhapaivatService.annaSeuraavaArkipaivaJosOnPyhaTaiViikonloppu(ajoaika) // Set date to always be a workday.

    tyo.ajoaika = this._timestampService.dateToTimestamp(safeTime)
    this.ajasta(tyo, firestore, batch)
  }

  private _getRandomMinutes() {
    return Math.floor(Math.random() * 60)
  }

  /** Poista ajastettu työ */
  public poistaAjastus(ajastettuTyoAvain: string, firestore: FirestoreProvider, batch: FirestoreWriteBatch) {
    const doc = firestore.annaDoc(this.collectionName + '/' + ajastettuTyoAvain)
    batch.delete(doc)
  }

  /** Poista ajastettu työ olemassa olevalla työllä */
  public poistaAjastusTyolla<T>(ajastettuTyo: AjastettuTyo<T>, firestore: FirestoreProvider, batch: FirestoreWriteBatch) {
    this.poistaAjastus(ajastettuTyo.avain, firestore, batch)
  }
}
